import { Constants } from '@/constants/constants';

export const Util = {
	/**
	 * undefined || null || empty string: return false
	 * @return {boolean}
	 */
	isNullOrEmpty(...args: (string | number | null | undefined)[]) {
		const value = args[0];
		if (typeof value === 'undefined') {
			return true;
		}
		if (
			value === '' ||
			value === null ||
			value === 'null' ||
			value === undefined ||
			value === 'undefined' ||
			(typeof value === 'object' && !Object.keys(value).length)
		) {
			return String(value) !== String(0);
		}
		return false;
	},

	/**
	 * null or undefined: return empty string
	 * @return {string|any}
	 */
	/**
	 * null, undefined 또는 비어있는 값 처리
	 * @param args 검사할 값들 (가변 인자)
	 * @return {string | any} 비어있으면 빈 문자열 반환, 아니면 원래 값 반환
	 */
	isEmpty(...args: any[]): string | any {
		const value = args[0];

		// undefined 처리
		if (typeof value === 'undefined') {
			return '';
		}

		// null, 빈 문자열, 'null', 'undefined' 및 빈 객체 검사
		if (
			value === '' ||
			value === null ||
			value === 'null' ||
			value === undefined ||
			value === 'undefined' ||
			(typeof value === 'object' && value !== null && Object.keys(value).length === 0)
		) {
			if (String(value) !== '0') {
				return '';
			}
			return String(value);
		}

		return value;
	},

	/**
	 * 특수문자 포함 체크
	 * @param asValue
	 * @param _empty
	 * @return {boolean}
	 */
	isSpc(asValue: string, _empty = false) {
		const regExp = /[~!@#$%^&*()_+|<>?:{}]/;
		return regExp.test(asValue);
	},

	/**
	 * 이메일 형식 체크
	 * @param asValue 검사할 값 (문자열)
	 * @param empty 빈 값 허용 여부 (기본값: false)
	 * @return {boolean} 이메일 형식 여부 반환
	 */
	isEmail(asValue: string | null | undefined, empty = false): boolean {
		const regExp = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;

		// 빈 값 허용 시 검사
		if (empty && this.isNullOrEmpty(asValue)) {
			return true;
		}

		// 문자열 여부 확인 후 정규식 검사
		return typeof asValue === 'string' && regExp.test(asValue);
	},

	/**
	 * 핸드폰 번호 형식 체크
	 * @param asValue 검사할 값 (문자열)
	 * @param empty 빈 값 허용 여부 (기본값: false)
	 * @return {boolean} 핸드폰 번호 형식 여부 반환
	 */
	isCelluar(asValue: string | null | undefined, empty = false): boolean {
		const regExp = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;

		// 빈 값 허용 시 검사
		if (empty && this.isNullOrEmpty(asValue)) {
			return true;
		}

		// 값이 문자열인지 확인 후 검사
		return typeof asValue === 'string' && regExp.test(asValue);
	},

	/**
	 * 패스워드 유효성 검사(영문+특문 8~20자)
	 * @param asValue
	 * @return {boolean}
	 */
	passWordCheck_crefia(asValue: string) {
		const minMax = /^(?=.*[a-zA-Z])(?=.*[!@#$%^*+=-])(?=.*[0-9]).{8,20}$/;
		const kor = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
		return minMax.test(asValue) && !kor.test(asValue);
	},
	/**
	 * 패스워드 유효성 검사(영문+특문 7~18자)
	 * @param asValue
	 * @return {boolean}
	 */
	passWordCheck(asValue: string) {
		const minMax = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[$@$!%*#?&])[A-Za-z\d$@$!%*#?&].{7,18}$/;
		const kor = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
		return minMax.test(asValue) && !kor.test(asValue);
	},
	/**
	 * 패스워드 유효성 검사(영문+특문 6~15자)
	 * @param asValue
	 * @return {boolean}
	 */
	passWordCheck_CBM(asValue: string) {
		const minMax = /^(?=.*[a-zA-Z])(?=.*[!@#$%^*+=-])(?=.*[0-9]).{6,15}$/;
		const kor = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
		return minMax.test(asValue) && !kor.test(asValue);
	},

	/**
	 * 접속환경 모바일 여부
	 * @return {boolean}
	 */
	isMobile() {
		return /iphone|ipad|ipod|android/i.test(navigator.userAgent.toLowerCase());
	},

	/**
	 * 브라우저 타입
	 * @return {string}
	 */
	browserType() {
		const userAgent = navigator.userAgent.toLowerCase();
		if (userAgent.indexOf('android') > -1) {
			return 'android';
		}
		if (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipad') > -1 || userAgent.indexOf('ipod') > -1) {
			return 'ios';
		}
		return 'other';
	},

	/**
	 * yyyy-MM-dd 형식 체크
	 * @param asValue
	 * @return {boolean}
	 */
	isDateDashFormat(asValue: string) {
		const datePattern = /^(19|20)\d{2}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[0-1])$/;
		return datePattern.test(asValue);
	},

	/**
	 * yyyy-MM-dd HH:MM 형식 체크
	 * @param asValue
	 * @return {boolean}
	 */
	isDateTimeDashFormat(asValue: string) {
		const datetimePattern =
			/^(19|20)\d{2}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[0-1]) (0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
		return datetimePattern.test(asValue);
	},

	/**
	 * URL 형식 여부 검사
	 * @param asValue 검사할 문자열
	 * @param empty 빈 값 허용 여부 (기본값: false)
	 * @return {boolean} URL 형식인지 여부 반환
	 */
	isURL(asValue: string | null | undefined, empty = false): boolean {
		const urlPattern = /^(http|https):\/\/(\w+(:\w+)?@)?([\w.-]+)(:\d+)?(\/[\w#!:.?+=&%@!\-/]*)?$/;

		if (empty) {
			if (this.isNullOrEmpty(asValue)) {
				return true;
			}
		}

		return typeof asValue === 'string' && urlPattern.test(asValue);
	},

	/**
	 * 문자열 앞,뒤 구분값 추가
	 * @param origin
	 * @param separator
	 * @return {string}
	 */
	cover(origin = '', separator = '') {
		return `${separator}${origin}${separator}`;
	},

	replaceOnlyHans(value: string) {
		const reg = /[^가-힣]/g;
		return value.replace(reg, '');
	},

	/**
	 * 기본값 처리
	 * @param value
	 * @param defaultValue
	 * @return {string|*}
	 */
	setValue(value: null | undefined, defaultValue = '') {
		return value === null || value === undefined ? defaultValue : value;
	},

	/**
	 * 날짜 변환 함수
	 * @param {*} value yyyyMMDD형식의 년월일 문자열
	 * @return {string}
	 */
	convertStringToDate(value: string | null) {
		if (this.isNullOrEmpty(value)) {
			return '';
		}
		const yyyyMMDD = String(value);
		const year = parseInt(yyyyMMDD.substring(0, 4));
		const month = parseInt(yyyyMMDD.substring(4, 6)) - 1;
		const day = parseInt(yyyyMMDD.substring(6, 8));
		return new Date(year, month, day);
	},

	/**
	 * 날짜 변환 함수
	 * @param {*} value yyyyMMDD형식의 년월일 문자열
	 * @param {*} delimiter 구분문자 없을 경우 년,월,일 표시
	 */
	convertDate(value: string | null, delimiter = '') {
		if (this.isNullOrEmpty(value)) {
			return '';
		}
		const yyyyMMDD = String(value);
		const sYear = yyyyMMDD.substring(0, 4);
		const sMonth = yyyyMMDD.substring(4, 6);
		const sDay = yyyyMMDD.substring(6, 8);
		if (this.isNullOrEmpty(delimiter)) {
			if (sDay === '') {
				return `${sYear}년 ${sMonth}월`;
			}
			return `${sYear}년 ${sMonth}월 ${sDay}일`;
		}
		if (sDay === '') {
			return sYear + delimiter + sMonth;
		}
		return sYear + delimiter + sMonth + delimiter + sDay;
	},

	/**
	 * 날짜 변환 함수
	 * @param date Date 객체
	 * @param delimiter 날짜 구분자 (기본값: '.')
	 * @param delimiter2 시분초 구분자 (기본값: ':')
	 * @returns {string} yyyy.MM.DD hh:mm:ss 형식의 문자열
	 */
	convertDateWithHHmmss(date: Date, delimiter = '.', delimiter2 = ':'): string {
		// 현재 연도
		const year = date.getFullYear().toString(); // yyyy
		// 현재 월 (두 자리로 변환)
		const month = (1 + date.getMonth()).toString().padStart(2, '0'); // MM
		// 현재 일 (두 자리로 변환)
		const day = date.getDate().toString().padStart(2, '0'); // DD
		// 현재 시 (두 자리로 변환)
		const hours = date.getHours().toString().padStart(2, '0'); // hh
		// 현재 분 (두 자리로 변환)
		const minutes = date.getMinutes().toString().padStart(2, '0'); // mm
		// 현재 초 (두 자리로 변환)
		const seconds = date.getSeconds().toString().padStart(2, '0'); // ss

		return `${year}${delimiter}${month}${delimiter}${day} ${hours}${delimiter2}${minutes}${delimiter2}${seconds}`;
	},

	/**
	 * 날짜형식을 yyyy-MM-dd 변환
	 * @param value
	 * @param delimiter
	 * @param type full: yyyymmdd, year: yyyy, month: yyyymm
	 * @returns {string}
	 */
	toStringByFormatting(value: Date, delimiter = '-', type = 'full') {
		const leftPad = (val: number) => {
			if (val >= 10) {
				return val;
			}
			return `0${val}`;
		};

		const year = value.getFullYear();
		const month = leftPad(value.getMonth() + 1);
		const day = leftPad(value.getDate());
		if (type === 'full') {
			return [year, month, day].join(delimiter);
		} else if (type === 'year') {
			return year;
		} else {
			return [year, month].join(delimiter);
		}
	},

	/**
	 * 날짜형식에서 시분초 구하기
	 * @param value Date 객체
	 * @param delimiter 구분자 (기본값: ':')
	 * @returns {string} 시분초를 구분자로 이어 붙인 문자열
	 */
	toStringByDateFormatting(value: Date, delimiter = ':'): string {
		const hour = value.getHours().toString().padStart(2, '0');
		const minutes = value.getMinutes().toString().padStart(2, '0');
		const seconds = value.getSeconds().toString().padStart(2, '0');
		return [hour, minutes, seconds].join(delimiter);
	},

	/**
	 * Date포맷 문자열을 연월일+시분초 문자열로 변환
	 * @param value
	 * @return {string}
	 */
	toStringFromDateString(value: string | number | Date) {
		const date = value ? new Date(value) : new Date();
		const dateString = this.toStringByFormatting(date, '.');
		const hourString = this.toStringByDateFormatting(date);
		return `${dateString} ${hourString}`;
	},

	/**
	 * 특정날짜 기준으로 다른날짜 Date 구하기
	 * @param value 차이
	 * @param standard 기준날짜
	 * @return {Date} 날짜 반환
	 */
	getOneDay(value = 0, standard = new Date()) {
		return new Date(standard.getTime() - value * 24 * 60 * 60 * 1000);
	},

	/**
	 * 날짜 차이 구하기(일)
	 * @param date1 Date 객체
	 * @param date2 Date 객체
	 * @returns {number | undefined} 날짜 차이의 절대값 반환
	 */
	getDateDiff(date1: Date, date2: Date): number | undefined {
		if (!date2 || isNaN(date2.getTime())) {
			return;
		}
		const diffDate = date2.getTime() - date1.getTime();
		return Math.abs(diffDate / (1000 * 60 * 60 * 24));
	},

	/**
	 * 날짜 차이 구하기(월)
	 * @param date1 Date 포맷
	 * @param date2 Date 포맷
	 * @returns {number} 날짜차이의 절대값 반환
	 */
	getMnthDiff(date1: Date, date2: Date): number | undefined {
		if (!date2 || isNaN(date2.getFullYear())) {
			return undefined;
		}

		return Math.abs((date2.getFullYear() - date1.getFullYear()) * 12 + (date2.getMonth() - date1.getMonth()));
	},

	/**
	 * 날짜 차이 유효성
	 * @param standard 기준 날짜
	 * @param validator 검증 날짜
	 * @return {String} present, past, future
	 */
	dateValidation(standard: Date, validator: Date): 'present' | 'past' | 'future' {
		const standardYear = standard.getFullYear();
		const standardMonth = standard.getMonth() + 1; // 0부터 시작하므로 +1
		const standardDate = standard.getDate();

		const validatorYear = validator.getFullYear();
		const validatorMonth = validator.getMonth() + 1;
		const validatorDate = validator.getDate();

		// 연도 비교
		if (standardYear < validatorYear) {
			return 'future';
		}
		if (standardYear > validatorYear) {
			return 'past';
		}

		// 월 비교
		if (standardMonth < validatorMonth) {
			return 'future';
		}
		if (standardMonth > validatorMonth) {
			return 'past';
		}

		// 일 비교
		if (standardDate < validatorDate) {
			return 'future';
		}
		if (standardDate > validatorDate) {
			return 'past';
		}

		// 모두 같으면 현재
		return 'present';
	},

	isFuture(standard: Date, validator: Date) {
		return Util.dateValidation(standard, validator) === 'future';
	},

	/**
	 * 성별 변환 함수 (1-> 남자, 2-> 여자)
	 * @param {*} value 성별(1,2)
	 */
	convertGenderToKorean(value: string | number | null) {
		if (this.isNullOrEmpty(value)) {
			return '';
		}
		if (value === 1) {
			return '남자';
		}
		if (value === 2) {
			return '여자';
		}
	},

	/**
	 * 성별 코드 반환
	 * @param value
	 * @return {string|number}
	 */
	convertGenderToNumber(value: string | null) {
		if (this.isNullOrEmpty(value)) {
			return '';
		}
		if (value === '남자') {
			return 1;
		}
		if (value === '여자') {
			return 2;
		}
		return '0';
	},

	/**
	 * 로컬 고유 문자열 처리
	 * @param arg 문자열 또는 숫자
	 * @return {string} 로컬 형식으로 변환된 문자열
	 */
	convertLocaleString(arg: string | number | null | undefined): string {
		if (this.isNullOrEmpty(arg)) {
			return '0';
		}
		return Number(arg).toLocaleString();
	},

	/**
	 * 파일명 반환
	 * @param path 파일 경로 문자열
	 * @return {string} 파일명 반환
	 */
	getFileName(path: string | null | undefined): string {
		if (!path) {
			// path가 null, undefined, 또는 ''인 경우
			return '';
		}
		const arSplitUrl = path.split('/');
		const nArLength = arSplitUrl.length;

		return arSplitUrl[nArLength - 1] || ''; // 마지막 요소가 undefined일 경우 대비
	},

	/**
	 * 숫자 자릿수 처리
	 * @param width
	 * @param str
	 * @return {*|string}
	 */
	fillZero(width: number, str: string | any[]) {
		return str.length >= width ? str : new Array(width - str.length + 1).join('0') + str;
	},

	/**
	 * return date format string
	 * @param date
	 * @param delimiter
	 * @return {string}
	 */
	getFormatDate(date: { getFullYear: () => any; getMonth: () => number; getDate: () => any }, delimiter = '-') {
		const year = date.getFullYear(); // yyyy
		let month: number | string = 1 + date.getMonth(); // M
		month = month >= 10 ? month : `0${month}`; // month 두자리로 저장
		let day = date.getDate(); // d
		day = day >= 10 ? day : `0${day}`; // day 두자리로 저장
		return year + delimiter + month + delimiter + day; // '-' 추가하여 yyyy-mm-dd 형태 생성 가능
	},

	/**
	 * 필수 입력값 체크
	 * @param {*} inputs 입력값
	 * @param data
	 * @return {string} 메세지
	 */
	async checkRequiredInput(inputs: { [x: string]: any }, data: { [s: string]: unknown } | ArrayLike<unknown>) {
		const dataKey = Object.keys(data);
		const dataValue = Object.values(data);

		let msg: unknown = '';
		for (let i = 0; i < dataKey.length; i++) {
			const value = inputs[dataKey[i]];
			switch (typeof value) {
				case 'string':
					if (this.isNullOrEmpty(value)) {
						msg = dataValue[i] === '' ? msg : dataValue[i];
						return msg;
					}
					break;
				case 'number':
					if (Number.isNaN(value) || value === -1) {
						msg = dataValue[i] === '' ? msg : dataValue[i];
						return msg;
					}
					break;
				default:
					if (!value) {
						msg = dataValue[i] === '' ? msg : dataValue[i];
						return msg;
					}
			}
		}
		return msg;
	},

	/**
	 * 랜덤 비밀번호 생성
	 * @param length
	 */
	randomCreate(length: number) {
		let randomStr = '';
		// 첫글자는 영문으로
		const firstString =
			'a,b,c,d,e,f,g,h,i,j,k,l,m,n,o,p,q,r,s,t,u,v,w,x,y,z,A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z'.split(
				','
			);
		randomStr = firstString[Math.floor(Math.random() * firstString.length)];
		// 두번째는 숫자
		const SecondString = '0,1,2,3,4,5,6,7,8,9'.split(',');
		randomStr += SecondString[Math.floor(Math.random() * SecondString.length)];
		// 영문, 숫자, 특수문자 포함된 랜덤 값
		const string =
			'0,1,2,3,4,5,6,7,8,9,a,b,c,d,e,f,g,h,i,j,k,l,m,n,o,p,q,r,s,t,u,v,w,x,y,z,A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z,!,@,#,$,%,&,*,'.split(
				','
			);
		for (let j = 0; j < length - 3; j++) {
			randomStr += string[Math.floor(Math.random() * string.length)];
		}

		// 랜덤에서 특수 문자가 없을 경우를 대비해서 특수문자를 마지막 값으로 추가
		const lastString = '!,@,#,$,%,&,*'.split(',');
		randomStr += lastString[Math.floor(Math.random() * lastString.length)];

		return randomStr;
	},

	/**
	 * 배열 또는 객체를 FormData로 변환하는 함수
	 * @param data 객체 또는 배열
	 * @returns FormData 또는 원본 데이터
	 */
	convertFormData(data: Record<string, any> | null | undefined): FormData | Record<string, any> | null {
		if (typeof data === 'object' && data !== null) {
			const formData = new FormData();

			// 객체의 키와 값을 순회하며 FormData에 추가
			Object.entries(data).forEach(([key, value]) => {
				formData.append(key, value instanceof Blob ? value : String(value));
			});

			return formData;
		}

		return data ?? null;
	},

	// 타입 가드 함수: `Record<string, unknown>`인지 확인
	isRecord(data: any): data is Record<string, unknown> {
		return data !== null && typeof data === 'object';
	},

	/**
	 * 날짜 문자열 생성
	 * @returns 날짜 문자열
	 */
	getToDateString() {
		const today = new Date();
		return today.toISOString().replace('T', ' ').substring(0, 10);
	},

	/**
	 * 날짜 문자열 생성
	 * @returns 날짜 문자열
	 */
	getToDateTimeString() {
		const today = new Date();
		today.setHours(today.getHours() + 9);
		return today.toISOString().replace('T', ' ').substring(0, 19);
	},

	/**
	 * 배열에 키의 값으로 특정 배열 반환
	 * @param arr
	 * @param key
	 * @param value
	 * @returns {boolean}
	 */
	findArray(arr: any[], key: string | number, value: any) {
		function isApple(element: { [x: string]: any }) {
			if (element[key] === value) {
				return true;
			}
		}
		return arr.find(isApple);
	},

	/**
	 * 금액 형식
	 * @param amount
	 * @param suffix
	 * @return {string}
	 */
	amountFormat(amount: string, suffix = '') {
		if (this.isNullOrEmpty(amount)) {
			return '0';
		}
		amount = amount.toString().replace(/,/g, '');

		// 소숫점 처리
		const parts = amount.split('.');
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		const formattedAmount = parts.join('.');

		if (this.isNullOrEmpty(suffix)) {
			return formattedAmount;
		}
		return formattedAmount + suffix;
	},

	/**
	 * 금액 형식 -> 숫자로 리턴
	 * @param amount
	 * @return {string}
	 */
	amountToInt(amount: string) {
		if (this.isNullOrEmpty(amount)) {
			return '0';
		}
		return parseInt(amount.toString().replace(/,/g, ''));
	},

	/**
	 * 숫자 검열
	 * @param value
	 * @return {string|*}
	 */
	validateNumber(value: string | number) {
		if (typeof value === 'number') {
			return String(value)
				.replace(/(^0+)/, '')
				.replace(/[^0-9]/g, '');
		}
		return value.replace(/(^0+)/, '').replace(/[^0-9]/g, '');
	},

	/**
	 * 숫자 검열 (- 가 들어가는 경우)
	 * @param value
	 * @return {string|*}
	 */
	validateNumberWithBar(value: number | string): string {
		if (typeof value === 'number' || (typeof value === 'string' && /^-?\d*\.?\d*$/.test(value))) {
			return String(value).replace(/[^0-9-]/g, '');
		}
		return value.replace(/[^0-9-]/g, '');
	},

	/**
	 * 숫자 검열 (맨 앞에 -가 들어가는 경우)
	 * @param value
	 * @return {string|*}
	 */
	validateNumberStartBar(value: string | number) {
		value = value.toString().replace(/(^-?0+)/, '');
		if (value.startsWith('-')) {
			return `${value.charAt(0)}${value.slice(1).replace(/[^0-9]/g, '')}`;
		}
		return value.replace(/[^0-9]/g, '');
	},

	/**
	 * 숫자 검열 (맨 앞에 0이 들어가는 경우)
	 * @param value
	 * @return {string|*}
	 */
	validateNumberStartZero(value: string | number) {
		if (typeof value === 'number') {
			return String(value).replace(/[^0-9]/g, '');
		}
		return value.replace(/[^0-9]/g, '');
	},

	/**
	 * 특수문자 검열
	 * @param value
	 * @return {string}
	 */
	validateNotSpc(value: string) {
		return value.replace(/[{}[\]/?.,;:|)*~`!^\-_+<>@#$%&\\=('"]/gi, '');
	},

	/**
	 * Boolean -> String
	 * @param value
	 * @return {string}
	 */
	booleanToString(value: string) {
		return value ? 'Y' : 'N';
	},

	testFunction(param: any) {
		console.log('Util testFunction ==========', param);
	},

	stringifyNumber(value: number) {
		const special = [
			'zeroth',
			'first',
			'second',
			'third',
			'fourth',
			'fifth',
			'sixth',
			'seventh',
			'eighth',
			'ninth',
			'tenth',
			'eleventh',
			'twelfth',
			'thirteenth',
			'fourteenth',
			'fifteenth',
			'sixteenth',
			'seventeenth',
			'eighteenth',
			'nineteenth'
		];
		const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

		if (value < 20) return special[value];
		if (value % 10 === 0) return deca[Math.floor(value / 10) - 2] + 'ieth';
		return deca[Math.floor(value / 10) - 2] + 'y-' + special[value % 10];
	},

	/**
	 * ID 유효성 검사(영문소문자+숫자 6~15자)
	 * @param asValue
	 * @return {boolean}
	 */
	idCheck(asValue: string) {
		const minMax = /^[a-z0-9]{6,15}$/;
		const kor = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
		return minMax.test(asValue) && !kor.test(asValue);
	},

	/**
	 * 핸드폰 번호 배열 변환
	 * @param asValue
	 * @return {boolean}
	 */
	convertCelluarArray(asValue: string): string[] | null {
		if (asValue.indexOf('-') > 0) {
			return asValue.split('-');
		}

		switch (asValue.length) {
			case 10:
				return [asValue.slice(0, 3), asValue.slice(3, 6), asValue.slice(6)];
			case 11:
				return [asValue.slice(0, 3), asValue.slice(3, 7), asValue.slice(7)];
		}
		return null;
	},

	// URL 파라미터 Object로 반환
	getUrlParameter(): Record<string, string> {
		const parameterObject: Record<string, string> = {};
		const locate = window.location.href;

		if (locate.indexOf('?') === -1) {
			return parameterObject;
		}

		const parameter = locate.split('?')[1].split('#')[0];
		const paramArray = parameter.split('&');
		for (const param of paramArray) {
			const [key, value] = param.split('=');
			if (key) {
				parameterObject[key] = decodeURIComponent(value || '');
			}
		}

		return parameterObject;
	},

	/**
	 * 한자리 숫자 앞에 0 붙여서 반환
	 * @param num
	 * @return {string}
	 */
	digit(num: number) {
		let strNum = '';
		if (num < 10) {
			strNum = '0' + num;
		} else {
			strNum = String(num);
		}
		return strNum;
	},

	closest(el: Element | null, selector: string): Element | null {
		while (el) {
			if (el.matches(selector)) {
				return el;
			}
			el = el.parentElement;
		}
		return null;
	},

	// X가 a 부터 b까지 변할 때 y는 c부터 d까지 변한다
	linearFunc(x: number, a: number, b: number, c: number, d: number): number {
		const y = ((d - c) / (b - a)) * (x - a) + c;
		return y;
	},

	// 쿠키 가져오기
	getCookie(cname: string) {
		const name = cname + '=';
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) == ' ') c = c.substring(1);
			if (c.indexOf(name) != -1) return c.substring(name.length, c.length);
		}
		return '';
	},

	// 24시간 기준 쿠키 설정하기
	setCookie(cname: string, cvalue: string | number | boolean, exdays: number) {
		const todayDate = new Date();
		todayDate.setTime(todayDate.getTime() + exdays * 24 * 60 * 60 * 1000);
		const expires = 'expires=' + todayDate.toUTCString(); // UTC 기준의 시간에 exdays 인자로 받은 값에 의해 쿠키가 설정됩니다.
		document.cookie = cname + '=' + encodeURIComponent(cvalue) + '; path=/; expires=' + expires;
	},

	/**
	 * 월 더한 or 뺀 날짜 반환
	 * @param {*} asValue
	 * @param {*} plusMinus
	 * @returns {Date}
	 */
	plusMinusMonth(asValue: Date, plusMinus: number): Date {
		const newDate = new Date(asValue);
		newDate.setMonth(newDate.getMonth() + plusMinus);
		return newDate;
	},

	defineParam(org: any, src: any): any {
		for (const prop in src) {
			if (!Object.prototype.hasOwnProperty.call(src, prop)) continue;
			if (typeof org[prop] === 'object') {
				org[prop] = Array.isArray(org[prop]) ? src[prop].slice(0) : this.defineParam(org[prop], src[prop]);
			} else {
				org[prop] = src[prop];
			}
		}
		return org;
	},

	setElementAttribute(element: HTMLElement, attribute: string, data: string | boolean): void {
		if (element.tagName === 'DIV') {
			data ? element.setAttribute(attribute, Constants.empty) : element.removeAttribute(attribute);
		} else {
			(element as any)[attribute] = data;
		}
	},

	/**
	 * 사업자 등록번호 등 입력값에 구분자 추가
	 * @param input 입력값 (문자열)
	 * @param type 구분자 유형 (0 - 사업자번호 / 1 - 전화번호 / 2 - 주민번호 / 3 - 계좌번호 / 4 - 카드번호 / 5 - 국세청승인번호)
	 * @param masking 마스킹 여부 (기본값: false)
	 * @returns {string} 포맷된 문자열
	 */
	formatInput(input: string, type: 0 | 1 | 2 | 3 | 4 | 5, masking = false): string {
		if (!Util.isNullOrEmpty(input)) {
			// 마스킹 여부에 따른 입력값 정리
			input = masking
				? input.replace(/[^0-9*-]/g, '') // 숫자와 *-만 허용
				: input.replace(/[^0-9]/g, ''); // 숫자만 허용

			switch (type) {
				case 0: // 사업자번호
					if (input.length > 6) {
						input = `${input.slice(0, 3)}-${input.slice(3, 5)}-${input.slice(5)}`;
					} else if (input.length > 3) {
						input = `${input.slice(0, 3)}-${input.slice(3)}`;
					}
					break;

				case 1: // 전화번호
					input = input
						.replace(/[^0-9]/g, '')
						.replace(/(^02.{0}|^01.{1}|[0-9]{3,4})([0-9]{3,4})([0-9]{4})/, '$1-$2-$3');
					break;

				case 2: // 주민번호
					if (input.length > 7) {
						input = `${input.slice(0, 6)}-${input.slice(6)}`;
					}
					break;

				case 3: // 계좌번호
					if (input.length > 12) {
						input = `${input.slice(0, 3)}-${input.slice(3, 5)}-${input.slice(5, 11)}-${input.slice(11)}`;
					} else if (input.length > 11) {
						input = `${input.slice(0, 3)}-${input.slice(3, 5)}-${input.slice(5)}`;
					} else if (input.length > 5) {
						input = `${input.slice(0, 3)}-${input.slice(3)}`;
					} else if (input.length > 3) {
						input = `${input.slice(0, 3)}-${input.slice(3)}`;
					}
					break;

				case 4: // 카드번호
					if (input.length > 13) {
						input = `${input.slice(0, 4)}-${input.slice(4, 8)}-${input.slice(8, 12)}-${input.slice(12)}`;
					} else if (input.length > 9) {
						input = `${input.slice(0, 4)}-${input.slice(4, 8)}-${input.slice(8)}`;
					} else if (input.length > 5) {
						input = `${input.slice(0, 4)}-${input.slice(4)}`;
					}
					break;

				case 5: // 국세청승인번호
					if (input.length === 24) {
						input = `${input.slice(0, 8)}-${input.slice(8, 16)}-${input.slice(16)}`;
					}
					break;
			}
		}
		return input;
	},

	/**
	 * 마스킹 함수
	 * type :
	 *   0 - 전화번호 (010-****-2222)
	 *   1 - 주민번호 (000203-1******)
	 *   2 - 계좌번호 (022-01-****)
	 *   3 - 주민번호 뒷자리 전체 마스킹 (000203-*******)
	 * @param number 문자열 형태의 숫자 (마스킹 대상)
	 * @param type 마스킹 유형 (0 | 1 | 2 | 3)
	 * @returns {string} 마스킹된 문자열
	 */
	maskingNum(number: string, type: 0 | 1 | 2 | 3): string {
		if (this.isNullOrEmpty(number)) {
			return number;
		}

		switch (type) {
			case 0: {
				// 전화번호 마스킹
				const values = number.split('-');
				if (values.length > 1) {
					values[1] = '*'.repeat(values[1].length); // 중간 자리 마스킹
					return values.join('-');
				}
				return number; // 구분자가 없으면 원본 반환
			}

			case 1: // 주민번호 (앞자리 + 첫 숫자 노출)
			case 3: {
				// 주민번호 전체 뒷자리 마스킹
				const cleanNumber = number.replace(/-/g, ''); // 구분자 제거
				if (cleanNumber.length > 7) {
					const visiblePart = cleanNumber.slice(0, 6); // 앞 6자리
					const maskedPart =
						type === 1
							? cleanNumber.slice(6, 7) + '******' // 첫 자리만 노출
							: '*******'; // 전체 마스킹
					return `${visiblePart}-${maskedPart}`;
				}
				return number; // 길이가 짧으면 원본 반환
			}

			case 2: {
				// 계좌번호 마스킹
				const splitNumber = number.split('-');
				const lastPart = splitNumber.at(-1) || '';
				const maskedPart = '*'.repeat(lastPart.length);
				return number.substring(0, number.length - lastPart.length) + maskedPart;
			}

			default:
				return number; // 정의되지 않은 type이면 원본 반환
		}
	},

	/**
	 * 날짜 형식 포맷 함수
	 * @param date 날짜 문자열 (YYYYMMDD 형식)
	 * @param type 구분자 (기본값: '-')
	 * @returns {string | null} 포맷된 날짜 문자열
	 */
	formattingDate(date: string | null | undefined, type = '-'): string | null {
		if (date && date.length === 8) {
			return date.slice(0, 4) + type + date.slice(4, 6) + type + date.slice(6, 8);
		}
		return null; // 날짜가 유효하지 않을 경우 null 반환
	},

	/**
	 * 말줄임 표시 함수
	 * @param str 문자열
	 * @param length 보여줄 최대 문자열 길이
	 * @returns {string} 말줄임 처리된 문자열
	 */
	ellipsis(str: string | null | undefined, length: number): string {
		if (!str) {
			return ''; // null 또는 undefined 처리
		}

		if (str.length > length) {
			return str.slice(0, length) + '...';
		}

		return str;
	}
};
